import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { googleReviewsAPI } from '../services/api';
import StarRating from './StarRating';
import '../styles/GoogleReviews.css';

const GoogleReviews = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await googleReviewsAPI.get();
        if (response.data.configured && response.data.reviews && response.data.reviews.length > 0) {
          setData(response.data);
        }
      } catch (error) {
        // Silent fail - section just won't show
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 320;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (loading || !data) return null;

  return (
    <div className="google-reviews-section">
      <div className="google-reviews-header">
        <div className="google-reviews-title">
          <svg className="google-logo" viewBox="0 0 24 24" width="24" height="24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          <h2>{t('googleReviews.title')}</h2>
        </div>
        <div className="google-reviews-summary">
          <StarRating rating={data.rating} size="medium" />
          <span className="google-reviews-score">{data.rating.toFixed(1)}</span>
          <span className="google-reviews-count">
            ({t('googleReviews.reviewCount', { count: data.totalReviews })})
          </span>
        </div>
      </div>

      <div className="google-reviews-carousel-wrapper">
        <button className="carousel-arrow carousel-arrow-left" onClick={() => scroll('left')} aria-label="Previous">
          &#8249;
        </button>
        <div className="google-reviews-carousel" ref={scrollRef}>
          {data.reviews.map((review, index) => (
            <div key={index} className="google-review-card">
              <div className="google-review-author">
                {review.profile_photo_url ? (
                  <img
                    src={review.profile_photo_url}
                    alt={review.author_name}
                    className="google-review-avatar"
                  />
                ) : (
                  <div className="google-review-avatar-placeholder">
                    {review.author_name?.charAt(0) || '?'}
                  </div>
                )}
                <div className="google-review-author-info">
                  <span className="google-review-name">{review.author_name}</span>
                  <span className="google-review-time">{review.relative_time_description}</span>
                </div>
              </div>
              <StarRating rating={review.rating} size="small" />
              {review.text && (
                <p className="google-review-text">{review.text}</p>
              )}
            </div>
          ))}
        </div>
        <button className="carousel-arrow carousel-arrow-right" onClick={() => scroll('right')} aria-label="Next">
          &#8250;
        </button>
      </div>

      <a
        href={`https://search.google.com/local/reviews?placeid=${encodeURIComponent(data.reviews[0]?.place_id || '')}`}
        target="_blank"
        rel="noopener noreferrer"
        className="google-reviews-link"
      >
        {t('googleReviews.seeAllOnGoogle')}
      </a>
    </div>
  );
};

export default GoogleReviews;
